import React from "react";
import { Lock, Play, Trophy, Swords, Dumbbell } from "lucide-react";
import { VISIBLE_LEVELS } from "@/game/gameData";
import { StarRow } from "@/components/Overlays";

const cardClass = (locked, practice) => {
  if (locked) return "border-[#2a2748] bg-[#0d0d1a] opacity-50 cursor-not-allowed";
  if (practice) return "border-[#ffb000] bg-[#121124] hover:bg-[#ffb000]/10 hover:shadow-[0_0_14px_rgba(255,176,0,0.4)] active:translate-y-0.5";
  return "border-[#00f0ff] bg-[#121124] hover:bg-[#00f0ff]/10 hover:shadow-[0_0_14px_rgba(0,240,255,0.4)] active:translate-y-0.5";
};

function LevelCard({ lv, locked, practice, stars, onStart }) {
  return (
    <button
      data-testid={`level-select-${lv.id}`}
      disabled={locked}
      onClick={() => onStart(lv.id, practice)}
      className={`flex items-center justify-between gap-2 rounded-lg border-2 p-2.5 text-left transition-all ${cardClass(locked, practice)}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-arcade text-[9px] text-cyan-300">LVL {lv.id}</span>
          <StarRow stars={stars} size={10} testid={`level-stars-${lv.id}`} />
        </div>
        <div className="font-vt text-base text-slate-200 leading-tight">{lv.name}</div>
      </div>
      {locked ? (
        <Lock size={16} className="text-slate-500 shrink-0" />
      ) : (
        <Play size={18} className={`${practice ? "text-[#ffb000]" : "text-[#39ff14]"} shrink-0`} />
      )}
    </button>
  );
}

export function ModeToggle({ practice, setPractice }) {
  const base = "flex items-center gap-1.5 font-arcade text-[9px] px-3 py-2 rounded-md transition-colors";
  const off = "text-slate-400 hover:text-slate-200";
  return (
    <div className="flex items-center gap-1 bg-[#0d0d1a] border border-[#2a2748] rounded-lg p-1" data-testid="mode-toggle">
      <button data-testid="mode-arcade-btn" onClick={() => setPractice(false)} className={`${base} ${practice ? off : "bg-[#ff007f] text-white"}`}>
        <Swords size={13} /> ARCADE
      </button>
      <button data-testid="mode-practice-btn" onClick={() => setPractice(true)} className={`${base} ${practice ? "bg-[#ffb000] text-[#06060e]" : off}`}>
        <Dumbbell size={13} /> PRACTICE
      </button>
    </div>
  );
}

export function LevelSelect({ player, practice, onStart }) {
  const stars = player.stars || {};
  return (
    <div className="w-full">
      <h2
        className={`font-arcade text-[10px] tracking-widest mb-3 text-center ${practice ? "text-[#ffb000]" : "text-emerald-400"}`}
        data-testid="level-select-heading"
      >
        {practice ? "PRACTICE ANY LEVEL · NO LIVES · NO GAME OVER" : "SELECT LEVEL"}
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {VISIBLE_LEVELS.map((lv) => (
          <LevelCard
            key={lv.id}
            lv={lv}
            locked={!practice && lv.id > player.unlocked_level}
            practice={practice}
            stars={stars[lv.id] || 0}
            onStart={onStart}
          />
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-[#121124] border border-[#2a2748] rounded-lg py-2">
      <div className="font-arcade text-[8px] text-slate-400 tracking-widest">{label}</div>
      <div data-testid={testid} className="font-vt text-2xl text-[#39ff14] text-glow-green">
        {value}
      </div>
    </div>
  );
}

export function PlayerStats({ player }) {
  const totalStars = VISIBLE_LEVELS.reduce((sum, lv) => sum + ((player.stars || {})[lv.id] || 0), 0);
  return (
    <div className="grid grid-cols-4 gap-3 w-full text-center">
      <Stat label="STARS" value={`${totalStars} / ${VISIBLE_LEVELS.length * 3}`} testid="stat-stars" />
      <Stat label="HIGH SCORE" value={player.high_score} testid="stat-highscore" />
      <Stat label="BEST WPM" value={Math.round(player.best_wpm)} />
      <Stat label="ACCURACY" value={`${Math.round(player.best_accuracy)}%`} />
    </div>
  );
}

export function Leaderboard({ board }) {
  if (!board.length) return null;
  return (
    <div className="w-full max-w-sm mt-8" data-testid="leaderboard">
      <h2 className="flex items-center justify-center gap-2 font-arcade text-[10px] text-[#ffb000] tracking-widest mb-3">
        <Trophy size={14} /> TOP PILOTS
      </h2>
      <div className="space-y-1">
        {board.map((p, i) => (
          <div key={p.id} className="flex items-center justify-between font-typewriter text-sm bg-[#121124] border border-[#2a2748] rounded px-3 py-1.5">
            <span className="text-slate-300">
              <span className="text-[#ffb000] mr-2">{i + 1}.</span>
              {p.name}
            </span>
            <span className="text-cyan-400 tracking-widest">{p.high_score}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
